import React from 'react'
import PropTypes from 'prop-types'
import { Button, Checkbox, MediaPlayer } from 'components/UIElements'
import { formatAndSanitizeHTML } from 'utils/misc'
import SurveyQuestion from '../SurveyQuestion'
import QuestionTitleImage, {
  hasQuestionTitleImage,
} from '../QuestionTitleImage'
import { RESPONSE_STATES } from 'utils/constants'
import MakeScrollable from '../MakeScrollable'
import QuestionFooter from '../QuestionFooter'
import {
  checkLogicAndComplete,
  getChoiceAnswerValue,
  getShouldRenderNextQuestion,
  resolveChoiceIdFromStoredAnswer,
} from '../../utils/instrumentUtils'

class SelectOne extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      selected: null,
      selectingChoice: null,
      deselectingChoice: null,
      completed: false,
    }
    this.firstButtonRef = React.createRef()
  }

  componentDidMount() {
    const { storedAnswer, suppressAutoAdvance } = this.props

    const restoredChoiceId = resolveChoiceIdFromStoredAnswer(
      this.props.question?.choices,
      storedAnswer?.[0],
    )
    if (restoredChoiceId) {
      this.onSelectChoice(restoredChoiceId)
      // State is set synchronously in onSelectChoice, complete immediately
      if (!suppressAutoAdvance) {
        this.onCompleteSelectOneQuestion()
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { selected } = this.state
    if (selected !== prevState.selected) {
      this.setState({ deselectingChoice: prevState.selected })
      // Animation cleanup handled by onAnimationEnd event
    }
  }

  componentWillUnmount() {
    // No cleanup needed - using event-driven approach
  }

  onSetCompletion = () => {
    const { selected } = this.state
    if (selected) {
      this.setState({ completed: true })
    } else {
      this.setState({ completed: false })
    }
  }

  handleAnimationEnd = (e) => {
    // Only handle our specific animation to avoid interfering with child animations
    if (e.animationName === 'selecting') {
      const { selectingChoice, deselectingChoice } = this.state
      if (selectingChoice) {
        this.setState({ selectingChoice: null })
      }
      if (deselectingChoice) {
        this.setState({ deselectingChoice: null })
      }
    }
  }

  onSelectChoice = (choiceId) => {
    const { setAllRequiredAnswered, id } = this.props
    this.setState(
      {
        selected: choiceId,
        selectingChoice: choiceId,
      },
      () => {
        setAllRequiredAnswered(false, id)
        this.onSetCompletion()
        // Autosave latest selection into Redux so sibling pages can enable Next
        // without requiring per-question footers.
        try {
          const { question, webSurvey, completeQuestion } = this.props
          const { label, type, attributes } = question
          const { required } = attributes
          const answer = this.createAnswerArr()
          const submission = {
            id,
            label,
            name: id,
            type,
            required,
            state: choiceId ? 'ANSWERED' : 'SKIPPED',
            answer,
          }
          if (typeof completeQuestion === 'function' && webSurvey) {
            completeQuestion(submission, webSurvey.order?.[0])
          }
        } catch (e) {
          console.warn('SelectOne autosave failed', { error: e })
        }
      },
    )
    // Animation cleanup handled by onAnimationEnd event
  }

  createAnswerArr = () => {
    const { question, id } = this.props
    const { choices } = question
    const { selected } = this.state
    if (selected) {
      return [
        {
          label: choices[selected].label,
          value: getChoiceAnswerValue(choices, selected),
          question: id,
          choice_id: selected,
        },
      ]
    }
    return []
  }

  onCompleteSelectOneQuestion = () => {
    const {
      question,
      id,
      completeQuestion,
      nextQ,
      webSurvey,
      submissionSurvey,
      nextQId,
      reviewFunctionalitySupported,
      isConsent = false,
      isLastQuestion = false,
      isSibling = false,
    } = this.props
    const { label, type, attributes } = question
    const { required } = attributes
    const answer = this.createAnswerArr()
    const submission = {
      id,
      label,
      name: id,
      type,
      required,
      state: RESPONSE_STATES.answered,
      answer,
    }
    const currentAnswer = {
      [id]: answer,
    }

    const shouldRenderNextQ = getShouldRenderNextQuestion({
      isConsent,
      isLastQuestion,
      isSibling,
    })

    checkLogicAndComplete(
      webSurvey,
      id,
      submissionSurvey,
      currentAnswer,
      nextQId,
      nextQ,
      completeQuestion,
      submission,
      reviewFunctionalitySupported,
    )
  }

  renderChoices = (choicesOrder, hasQuestionImage) => {
    const {
      question,
      id,
      renderBackButton,
      strings,
      submissionSurvey,
      webSurvey,
      isLastQuestion,
      isConsent,
      submissionLoading,
      isSibling = false,
    } = this.props
    const { choices, attributes, mediaSrc } = question
    const { required, disable_buttons: disableButtons } = attributes

    const { selected, selectingChoice, deselectingChoice, completed } =
      this.state
    let hasImages
    return (
      <div className={`question-choices${hasQuestionImage ? ' right' : ''}`}>
        <MakeScrollable>
          {choicesOrder.map((choiceId, idx) => {
            const choice = choices[choiceId]
            const { imageSrc } = choice
            if (imageSrc) hasImages = true
            return (
              <Button
                onClick={() => this.onSelectChoice(choiceId)}
                onAnimationEnd={this.handleAnimationEnd}
                key={`${choiceId}`}
                className={`choice${
                  selectingChoice === choiceId ? ' selecting' : ''
                }${selected === choiceId ? ' selected' : ''}${
                  deselectingChoice === choiceId ? ' deselecting' : ''
                }${hasImages ? ' column' : ''}${
                  disableButtons ? ' no-icon' : ''
                }`}>
                {!hasImages && !disableButtons && (
                  <Checkbox
                    checked={choiceId === selected}
                    transition={
                      choiceId === selectingChoice ||
                      choiceId === deselectingChoice
                    }
                  />
                )}
                {imageSrc && <img src={imageSrc} alt={`choice-${choiceId}`} />}
                <div className={`text ${hasImages ? '' : 'no-img'}`}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: formatAndSanitizeHTML(choice.label),
                    }}
                  />
                </div>
                {hasImages && !disableButtons && (
                  <Checkbox
                    checked={choiceId === selected}
                    transition={
                      choiceId === selectingChoice ||
                      choiceId === deselectingChoice
                    }
                  />
                )}
              </Button>
            )
          })}
          {!isSibling && (
            <QuestionFooter
              completed={completed}
              id={id}
              isConsent={isConsent}
              isLastQuestion={isLastQuestion}
              loading={submissionLoading}
              onComplete={() => {
                this.onCompleteSelectOneQuestion(selected)
              }}
              question={question}
              renderBackButton={renderBackButton}
              strings={strings}
              submissionSurvey={submissionSurvey}
              webSurvey={webSurvey}
            />
          )}
        </MakeScrollable>
      </div>
    )
  }

  render() {
    const { className, id, question } = this.props
    const { choices_order, label, imageSrc } = question
    const _className = className ? ` ${className}` : ''
    const hasQImage = hasQuestionTitleImage(imageSrc)

    return (
      <div ref={this.props.forwardedRef}>
        <SurveyQuestion id={id}>
          <MakeScrollable>
            <div
              className={`survey-question select one${_className}${
                hasQImage ? ' has-image' : ''
              }`}>
              <p className={`question-label${hasQImage ? ' left' : ''}`}>
                <span
                  dangerouslySetInnerHTML={{
                    __html: formatAndSanitizeHTML(label),
                  }}
                />
                <QuestionTitleImage imageSrc={imageSrc} questionId={id} />
              </p>
              {this.renderChoices(choices_order, hasQImage)}
            </div>
          </MakeScrollable>
        </SurveyQuestion>
      </div>
    )
  }
}

const ForwardedSelectOne = React.forwardRef((props, ref) => (
  <SelectOne {...props} forwardedRef={ref} />
))

export default ForwardedSelectOne

SelectOne.propTypes = {
  className: PropTypes.string,
  completeQuestion: PropTypes.func,
  id: PropTypes.string,
  nextQ: PropTypes.func,
  nextQId: PropTypes.func,
  question: PropTypes.object,
  renderBackButton: PropTypes.func,
  setAllRequiredAnswered: PropTypes.func,
  strings: PropTypes.objectOf(PropTypes.string),
  submissionSurvey: PropTypes.shape({
    questions: PropTypes.array,
  }),
  webSurvey: PropTypes.object,
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
}
